<template>
  <div ref="rootRef" class="dashboard-verdict-list-item">
    <!-- Skeleton shown until the item intersects the scroll viewport -->
    <q-skeleton
      v-if="!revealed"
      :height="`${VERDICT_CARD_HEIGHT}px`"
      class="dashboard-verdict-list-item__skeleton"
      aria-hidden="true"
    />
    <Transition v-else name="verdict-card-reveal" appear>
      <KdsDashboardVerdictCard v-bind="cardProps" />
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import KdsDashboardVerdictCard from './KdsDashboardVerdictCard.vue';
import { VERDICT_CARD_HEIGHT } from '@/kds/tokens';
import type { KdsBadgeTone, PersonaSegment } from '@/kds/types';

interface Props {
  // KdsDashboardVerdictCard props (forwarded)
  id: string;
  subject: string;
  protocol: string;
  timeAgo: string;
  headline: string;
  consensusTone: KdsBadgeTone;
  personaCount: number;
  avatarSegments?: PersonaSegment[];
  // Item index in the list — first 3 items bypass the skeleton immediately.
  index: number;
  // Optional scroll container element for IntersectionObserver root.
  scrollContainer?: HTMLElement | null;
}

const props = withDefaults(defineProps<Props>(), {
  avatarSegments: () => [],
  scrollContainer: null
});

const revealed = ref(false);
const rootRef = ref<HTMLElement | null>(null);

const cardProps = computed(() => ({
  id: props.id,
  subject: props.subject,
  protocol: props.protocol,
  timeAgo: props.timeAgo,
  headline: props.headline,
  consensusTone: props.consensusTone,
  personaCount: props.personaCount,
  avatarSegments: props.avatarSegments
}));

let observer: IntersectionObserver | null = null;

onMounted(() => {
  // Bypass skeleton for the first 3 items (initially visible — no flash).
  if (props.index < 3) {
    revealed.value = true;
    return;
  }

  // Defensive guard for SSR / older browsers without IntersectionObserver.
  if (typeof IntersectionObserver === 'undefined') {
    revealed.value = true;
    return;
  }

  // Initialise IntersectionObserver with 40px bottom margin for pre-loading.
  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting) {
        revealed.value = true;
        // Stop observing once revealed — the card stays mounted.
        observer?.disconnect();
        observer = null;
      }
    },
    {
      root: props.scrollContainer ?? null,
      rootMargin: '0px 0px 40px 0px',
      threshold: 0
    }
  );

  // Observe the wrapper element (acts as the sentinel while skeleton is shown).
  if (rootRef.value) {
    observer.observe(rootRef.value);
  }
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
});
</script>

<style scoped lang="scss">
.dashboard-verdict-list-item {
  // Preserve the expected card height while the skeleton is shown,
  // so the scroll container's layout stays stable during reveal.
  height: var(--verdict-card-height, 152px);
}

.dashboard-verdict-list-item__skeleton {
  border-radius: var(--r-4);
}

// Reveal transition — fade only, no translateY (skeleton is already positioned).
.verdict-card-reveal-enter-active {
  transition: opacity 150ms var(--ease-out);
}

.verdict-card-reveal-enter-from {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .verdict-card-reveal-enter-active {
    transition: none;
  }
}
</style>
